import React, { FC } from 'react'

import {
  DocumentHead,
  LoadingState,
  Pill,
  useForTablet,
  Tabs,
  Tab,
  TabList,
  TabPanel,
  ErrorMessage,
  DocumentHeader,
} from '@sketch/components'
import { DocumentItemsLayout } from '../../../shares/components/DocumentItemsLayout'
import { Footer } from '../../../shares/components/DocumentItemsLayout/DocumentItemsLayout.styles'

import {
  HeaderAfterCollections,
  DocumentsWrapper,
  TabPanelContent,
  MobileWrapper,
} from './ArchiveList.styles'

import {
  GetStashedStorageItemsDocument,
  GetStashedStorageItemsQuery,
  WorkspaceMinimalFragment,
  useGetDocumentListSettingsQuery,
} from '@sketch/gql-types'
import { ScrollToTop } from '../../../../components/ScrollToTopButton'
import NestedProjectItemsLayout from '../../../nestedProjects/components/NestedProjectItemsLayout'
import NestedProjectDropdown from '../../../nestedProjects/components/NestedProjectDropdown'
import { useGetStashedStorageItems } from '../../operations'
import ShareEmptyState from '../../../shares/components/ShareEmptyState'
import { LayoutPortal, useSearch } from '@sketch/utils'
import { workspaceSidebarLayoutSymbols } from '../../../workspace/components'
import { isProject, isShare } from '../../utils/filterTypes'
import { DocumentItemDropdown } from '../../../shares/components/DocumentItemDropdown'
import {
  isNestedProject,
  updatePinnedShares,
} from '../../../shares/components/ShareActionsDropdown/pinShare.cache'
import { PROJECTS_LAYOUT_COLUMNS } from '../../constants'

export interface ArchiveListProps {
  workspace: WorkspaceMinimalFragment
}
export const ArchiveList: FC<ArchiveListProps> = props => {
  const { workspace } = props
  const { searchDebounced: search } = useSearch()

  const isTabletAndBigger = useForTablet()
  const isMobile = !isTabletAndBigger

  const { identifier: workspaceId } = workspace

  const result = useGetStashedStorageItems({
    workspaceId,
    search,
    source: 'ARCHIVE',
  })

  // Load the document layout
  const { data: documentListSettingsData } = useGetDocumentListSettingsQuery()
  const { documentsLayout } = documentListSettingsData || {}

  if (result.kind === 'loading') return <LoadingState />
  if (!result || result.kind === 'error') return <ErrorMessage.Generic />

  const { items: storageItems, totalCounts, loading, fetchMore } = result

  const nestedProjects = storageItems?.filter(isProject) || []
  const shares = storageItems?.filter(isShare) || []

  const hasShares = !!shares.length
  const hasNestedProjects = !!nestedProjects.length

  const documentHeader = isTabletAndBigger && (
    <>
      <DocumentHead title="Archive" />
      <LayoutPortal token={workspaceSidebarLayoutSymbols.header}>
        <DocumentHeader
          title="Archived Items"
          description="A place for documents or folders you’re not actively working on."
          learnMoreTooltipTitle="Learn more about archive"
          learnMoreTooltipURL="https://www.sketch.com/redirect/web/archive-projects"
        />
      </LayoutPortal>
      <LayoutPortal token={workspaceSidebarLayoutSymbols.headerSlim}>
        <DocumentHeader title="Archived Items" />
      </LayoutPortal>
    </>
  )

  // Project Empty State
  if (!loading && totalCounts.storageItems === 0) {
    return (
      <>
        {documentHeader}

        <ShareEmptyState
          search={search}
          workspace={workspace}
          filter="ARCHIVE_PROJECTS"
        />
      </>
    )
  }

  const renderDocumentsList = () => {
    if (!hasShares) {
      return null
    }

    const isGridLayout = documentsLayout === 'GRID'

    return (
      <DocumentsWrapper
        listLayout={!isGridLayout}
        // return list without gradient styling when there's no nested projects
        grouped={hasNestedProjects}
      >
        {/**
         * This header should only be seen when the user has both shares and
         * nested projects and is on the grid view
         */}
        {hasNestedProjects && isGridLayout && isTabletAndBigger && (
          <HeaderAfterCollections>
            Documents
            <Pill variant="secondary">{totalCounts.shares}</Pill>
          </HeaderAfterCollections>
        )}

        <DocumentItemsLayout
          workspaceId={workspace.identifier}
          shares={shares}
          // TODO: Remove `showProjectName` prop when `nested-projects` is 100% enabled
          // https://linear.app/sketch/issue/SWEB-768/cleanup-after-nested-projects-ff-is-100percent-enabled
          showProjectName={true}
          showCollectionName={false}
          totalCount={totalCounts.shares}
          onLoadMore={fetchMore}
          renderDropdown={share => {
            return (
              <DocumentItemDropdown
                share={share}
                workspaceIdentifier={workspace.identifier}
                workspace={workspace}
                pinDocumentConfig={{
                  shareIdentifier: share.identifier,
                  pinned: !!share?.pinnedByCurrentUserAt,
                  update(cache, { data }) {
                    if (!data) return

                    updatePinnedShares<GetStashedStorageItemsQuery>({
                      shareId: share.identifier,
                      pickPagination: data => data?.workspace.storageItems,
                      readData: () => {
                        return (
                          cache.readQuery<GetStashedStorageItemsQuery>({
                            query: GetStashedStorageItemsDocument,
                            variables: result.variables,
                          }) ?? undefined
                        )
                      },
                      writeData: newData => {
                        cache.writeQuery({
                          query: GetStashedStorageItemsDocument,
                          variables: result.variables,
                          data: newData,
                        })
                      },
                      sort: 'ARCHIVED_AT_DESC',
                    })
                  },
                }}
              />
            )
          }}
        />
      </DocumentsWrapper>
    )
  }

  const renderNestedProjectsList = () => {
    if (!hasNestedProjects) return null

    return (
      <>
        <NestedProjectItemsLayout
          workspaceId={workspaceId}
          nestedProjects={nestedProjects}
          onLoadMore={fetchMore}
          totalCount={totalCounts.projects}
          customColumns={PROJECTS_LAYOUT_COLUMNS}
          renderDropdown={item => {
            if (!isNestedProject(item)) return

            return (
              <NestedProjectDropdown
                workspace={workspace}
                nestedProject={item}
              />
            )
          }}
        />
        <ScrollToTop />
      </>
    )
  }

  const title = 'Archived Items'

  // For mobile view we want to show tabs
  if (isMobile && hasNestedProjects) {
    return (
      <MobileWrapper>
        <DocumentHead title={title} />

        <Tabs>
          <TabList>
            <Tab>
              Folders{' '}
              {totalCounts.projects && (
                <Pill variant="secondary">{totalCounts.projects}</Pill>
              )}
            </Tab>
            <Tab>
              Documents{' '}
              {totalCounts.shares && (
                <Pill variant="secondary">{totalCounts.shares}</Pill>
              )}
            </Tab>
          </TabList>
          <TabPanel>
            <TabPanelContent>
              {renderNestedProjectsList()}
              <Footer />
            </TabPanelContent>
          </TabPanel>
          <TabPanel>
            <TabPanelContent>{renderDocumentsList()}</TabPanelContent>
          </TabPanel>
        </Tabs>
      </MobileWrapper>
    )
  }

  return (
    <>
      {documentHeader}

      {renderNestedProjectsList()}
      {renderDocumentsList()}
    </>
  )
}
